/**
 * MilestoneSubmittalMount — a milestone's linked Submittal, mounted inline under
 * MilestoneSections when the milestone's deliverable type is the Submittal Review template
 * (isSubmittalMilestoneType).
 *
 * Mirrors MilestoneAsiMount.jsx / MilestoneRfiMount.jsx exactly (Submittals run the same way as
 * ASIs/RFIs — see docs/PASSENGERS.md). The reverse direction (Submittal -> milestone) is
 * CaMilestoneMount in ConstructionSection.jsx.
 *
 * Lookup: Supabase reverse query by milestone_airtable_id (getSubmittalByMilestone), cached in
 * ctx.milestoneSubmittalCache[msId] as the submittal row or false (no match). The contract record
 * comes from ctx.contractCache (loaded on demand, same as the sibling CA mounts).
 *
 * Renders null for milestone types that aren't the Submittal Review template.
 */

import { useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { isSubmittalMilestoneType } from '../outline/constants.js'
import { getSubmittalByMilestone } from '../services/submittals.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { SubmittalNode } from './ConstructionSection.jsx'

function ensureContractsLoaded(projId) {
  if (!projId) return
  if (ctx.contractCache !== undefined) return
  ctx.contractCache = null
  getProjectContracts(projId).then(c => {
    ctx.contractCache = c
    window.dispatchEvent(new Event('outline-render'))
  }).catch(() => {
    ctx.contractCache = []
    window.dispatchEvent(new Event('outline-render'))
  })
}

export default function MilestoneSubmittalMount({ ms, projectId }) {
  // The type check takes the Deliverable Type record ID since the milestone-type-ids change; passing
  // the type NAME made this always false, so no linked record ever showed (fixed 2026-09-24).
  const typeName = (ms?.fields?.['Deliverable'] || [])[0] || ''
  const msId = ms?.id || ''
  const isSubmittalMs = isSubmittalMilestoneType(typeName)
  const loadedRef = useRef(false)

  function fetchSubmittal() {
    if (!ctx.milestoneSubmittalCache) ctx.milestoneSubmittalCache = {}
    if (ctx.milestoneSubmittalCache[msId] !== undefined) return
    ctx.milestoneSubmittalCache[msId] = null // in-flight
    getSubmittalByMilestone(msId).then(s => {
      if (!ctx.milestoneSubmittalCache) ctx.milestoneSubmittalCache = {}
      ctx.milestoneSubmittalCache[msId] = s || false
      if (s && s.project_id) ensureContractsLoaded(s.project_id)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.milestoneSubmittalCache) ctx.milestoneSubmittalCache = {}
      ctx.milestoneSubmittalCache[msId] = false
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Lazy-load the linked submittal once per milestone (hooks before any return)
  useEffect(() => {
    if (!isSubmittalMs || !msId || loadedRef.current) return
    loadedRef.current = true
    fetchSubmittal()
  }, [isSubmittalMs, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a cache bust (e.g. onChanged from an edit made via this mount).
  useEffect(() => {
    if (!isSubmittalMs || !msId) return
    function onRender() {
      if (!loadedRef.current) return
      if (ctx.milestoneSubmittalCache?.[msId] === undefined) fetchSubmittal()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [isSubmittalMs, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isSubmittalMs || !msId) return null

  const entry = ctx.milestoneSubmittalCache?.[msId]
  if (entry === undefined || entry === null) return null // not started / in-flight — quiet
  if (entry === false) return null // no submittal linked to this milestone

  const submittal = entry
  const contracts = Array.isArray(ctx.contractCache) ? ctx.contractCache : []
  const contract = contracts.find(c => c.id === submittal.contract_id)
    || { id: submittal.contract_id, project_id: submittal.project_id || projectId || wf.zoomProjectId || '' }

  function onChanged() {
    if (ctx.milestoneSubmittalCache) ctx.milestoneSubmittalCache[msId] = undefined
    if (ctx.submittalCache && submittal.contract_id) ctx.submittalCache[submittal.contract_id] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  return <SubmittalNode submittal={submittal} contract={contract} onChanged={onChanged} hideMilestoneMount labelPrefix="Submittal: " mountCtx={'ms:' + msId} />
}
